import { useCallback, useEffect, useRef, useState } from "react";
import {
  deregisterRestaurantFcmToken,
  clearStoredRestaurantFcmTokensAfterRegistrations,
  activateRestaurantPushSession,
  registerRestaurantFcmToken,
  retryRestaurantFcmTokenCleanup,
  runRestaurantPushRegistrationAttempt,
} from "@/lib/api/notifications";
import {
  requestRestaurantPushToken,
  supportsRestaurantPush,
  type PushTokenFailureReason,
  type PushTokenResult,
} from "@/lib/firebase/messaging";

export type RestaurantPushStatus =
  | "inactive"
  | "unsupported"
  | "prompt"
  | "denied"
  | "registering"
  | "registered"
  | "error";

export type RestaurantPushFailureReason =
  | PushTokenFailureReason
  | "permission-request"
  | "registration"
  | "deregistration";

export type PushRegistrationAdapter = {
  isSupported: () => boolean;
  getPermission: () => NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  requestToken: () => Promise<PushTokenResult>;
  register: (token: string, storeId: string, ownerId: string) => Promise<void>;
  deregister: (storeId: string, ownerId: string | undefined) => Promise<void>;
  retryCleanup: (ownerId: string, currentToken: string) => Promise<void>;
  clearLocal: () => Promise<void>;
};

export const browserPushRegistrationAdapter: PushRegistrationAdapter = {
  isSupported: supportsRestaurantPush,
  getPermission: () => Notification.permission,
  requestPermission: () => Notification.requestPermission(),
  requestToken: requestRestaurantPushToken,
  register: registerRestaurantFcmToken,
  deregister: deregisterRestaurantFcmToken,
  retryCleanup: retryRestaurantFcmTokenCleanup,
  clearLocal: clearStoredRestaurantFcmTokensAfterRegistrations,
};

export function usePushRegistration({
  sellerId,
  storeId,
  adapter = browserPushRegistrationAdapter,
}: {
  sellerId: string | undefined;
  storeId: string | undefined;
  adapter?: PushRegistrationAdapter;
}) {
  const [status, setStatus] = useState<RestaurantPushStatus>("inactive");
  const [lastError, setLastError] =
    useState<RestaurantPushFailureReason | null>(null);
  const generationRef = useRef(0);
  const activeSellerRef = useRef(sellerId);
  const removedScopeRef = useRef<string | null>(null);
  const registeredKeysRef = useRef(new Set<string>());
  const inFlightRef = useRef<{
    scopeKey: string;
    promise: Promise<void>;
  } | null>(null);

  const ensureRegistered = useCallback(async () => {
    if (!sellerId || !storeId) {
      setStatus("inactive");
      setLastError(null);
      return;
    }
    const scopeKey = `${sellerId}:${storeId}`;
    if (removedScopeRef.current === scopeKey) {
      setStatus("inactive");
      return;
    }
    if (!adapter.isSupported()) {
      setStatus("unsupported");
      setLastError("unsupported");
      return;
    }
    const permission = adapter.getPermission();
    if (permission === "denied") {
      setStatus("denied");
      setLastError("permission");
      return;
    }
    if (permission === "default") {
      setStatus("prompt");
      setLastError(null);
      return;
    }
    const generation = generationRef.current;
    if (inFlightRef.current?.scopeKey === scopeKey) {
      return inFlightRef.current.promise;
    }
    setStatus("registering");
    setLastError(null);
    const run = runRestaurantPushRegistrationAttempt(async (isLifecycleCurrent) => {
      const result = await adapter.requestToken();
      if (generation !== generationRef.current || !isLifecycleCurrent()) return;
      if (!result.ok) {
        setLastError(result.reason);
        setStatus(
          result.reason === "permission"
            ? "denied"
            : result.reason === "unsupported"
              ? "unsupported"
              : "error",
        );
        return;
      }

      const key = `${scopeKey}:${result.token}`;
      if (registeredKeysRef.current.has(key)) {
        await adapter.retryCleanup(sellerId, result.token);
        if (generation !== generationRef.current || !isLifecycleCurrent()) return;
        setStatus("registered");
        return;
      }

      // registerRestaurantFcmToken owns rotation ordering: POST replacement,
      // persist it locally, then retry cleanup of the old token. Never DELETE
      // the only working token before replacement registration succeeds.
      await adapter.register(result.token, storeId, sellerId);
      if (generation !== generationRef.current || !isLifecycleCurrent()) return;
      registeredKeysRef.current.add(key);
      setStatus("registered");
    })
      .catch((error) => {
        console.warn(
          "[restaurant-notifications] Push registration failed",
          error,
        );
        if (generation === generationRef.current) {
          setLastError("registration");
          setStatus("error");
        }
      })
      .finally(() => {
        if (inFlightRef.current?.promise === run) inFlightRef.current = null;
      });
    inFlightRef.current = { scopeKey, promise: run };
    return run;
  }, [adapter, sellerId, storeId]);

  useEffect(() => {
    if (sellerId) activateRestaurantPushSession();
  }, [sellerId]);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    const previousSeller = activeSellerRef.current;
    activeSellerRef.current = sellerId;
    removedScopeRef.current = null;
    const ownerChanged = Boolean(
      previousSeller && previousSeller !== sellerId,
    );
    const previousRun = ownerChanged ? inFlightRef.current?.promise : undefined;
    queueMicrotask(() => {
      void (async () => {
        await previousRun?.catch(() => undefined);
        if (generation !== generationRef.current) return;
        if (ownerChanged) {
          try {
            await adapter.clearLocal();
          } catch (error) {
            console.warn(
              "[restaurant-notifications] Local owner cleanup failed",
              error,
            );
          }
        }
        if (generation === generationRef.current) await ensureRegistered();
      })();
    });
  }, [adapter, ensureRegistered, sellerId]);

  useEffect(
    () => () => {
      // Logical cancellation: a late getToken result cannot reach POST and a
      // completed POST cannot mutate state. Explicit remove waits for an
      // already-started POST before DELETE; logout's API helper does the same.
      generationRef.current += 1;
    },
    [],
  );

  useEffect(() => {
    const retryWhenOnline = () => void ensureRegistered();
    const retryWhenVisible = () => {
      if (document.visibilityState === "visible") void ensureRegistered();
    };
    window.addEventListener("online", retryWhenOnline);
    document.addEventListener("visibilitychange", retryWhenVisible);
    return () => {
      window.removeEventListener("online", retryWhenOnline);
      document.removeEventListener("visibilitychange", retryWhenVisible);
    };
  }, [ensureRegistered]);

  const requestPermissionAndRegister = useCallback(async () => {
    if (!adapter.isSupported()) {
      setStatus("unsupported");
      return;
    }
    let permission: NotificationPermission;
    try {
      permission = await adapter.requestPermission();
    } catch (error) {
      console.warn(
        "[restaurant-notifications] Notification permission request failed",
        error,
      );
      setLastError("permission-request");
      setStatus("error");
      return;
    }
    if (permission === "denied") {
      setStatus("denied");
      setLastError("permission");
      return;
    }
    await ensureRegistered();
  }, [adapter, ensureRegistered]);

  const removeRegistration = useCallback(async () => {
    if (!storeId) return;
    const scopeKey = sellerId ? `${sellerId}:${storeId}` : null;
    generationRef.current += 1;
    removedScopeRef.current = scopeKey;
    try {
      await inFlightRef.current?.promise.catch(() => undefined);
      await adapter.deregister(storeId, sellerId);
      registeredKeysRef.current.clear();
      setLastError(null);
      setStatus("inactive");
    } catch (error) {
      console.warn(
        "[restaurant-notifications] Push deregistration failed",
        error,
      );
      setLastError("deregistration");
      setStatus("error");
    }
  }, [adapter, sellerId, storeId]);

  const retry = useCallback(() => {
    removedScopeRef.current = null;
    return ensureRegistered();
  }, [ensureRegistered]);

  return {
    status,
    lastError,
    retry,
    requestPermissionAndRegister,
    removeRegistration,
  };
}
